import { spotify } from '../core/spotify.js';
import { openExternal, qrSvg } from '../core/external.js';
import { notify } from '../core/notify.js';
import { t } from '../core/i18n.js';
import { device } from '../core/device.js';
import { escapeHtml } from '../core/utils.js';

/* Botón «Banda sonora en Spotify» de la ficha y fila «Spotify» de Ajustes.
   - Sin Spotify configurado en el servidor: el botón abre la búsqueda en Spotify (enlace), como siempre.
   - Configurado pero sin conectar: se propone conectar (QR / enlace para el móvil) o abrir el enlace.
   - Conectado: se busca el álbum y suena en el aparato activo de Spotify; si no hay ninguno, se elige uno. */

function dialog({ title, text, buttons }) {
    const app = window.app;
    const dlg = app.dialog.create({ title: escapeHtml(title), text: escapeHtml(text), cssClass: 'spotify-dialog', buttons: buttons.filter(Boolean) });
    dlg.open();
    return dlg;
}

/** Conecta Spotify en este aparato: código de enlace + QR; espera a que el móvil termine. Devuelve la sesión o null. */
export async function linkSpotify() {
    const app = window.app;
    let link;
    try {
        link = await spotify.startLink();
    } catch (e) {
        notify.error(t('spotify.failed'));
        return null;
    }
    const short = link.url.replace(/^https?:\/\//, '');
    let cancelled = false;
    const dlg = app.dialog.create({
        cssClass: 'spotify-dialog',
        title: escapeHtml(t('spotify.linkTitle')),
        content: `<div class="phone-body"><p class="pw-note">${escapeHtml(t('spotify.linkText'))}</p>
            <div class="phone-grid"><div class="pair-qr phone-qr" aria-hidden="true">${qrSvg(link.url)}</div>
            <div class="phone-side"><div class="phone-url" style="word-break:break-all;font-size:14px">${escapeHtml(short)}</div>
            <p class="pw-note" style="margin-top:10px"><span class="mi" style="vertical-align:-6px">hourglass_top</span> ${escapeHtml(t('spotify.waiting'))}</p></div></div></div>`,
        buttons: [
            device.isTV ? null : { text: t('spotify.openLink'), close: false, onClick: () => { openExternal(link.url); } },
            { text: t('common.cancel'), onClick: () => { cancelled = true; } }
        ].filter(Boolean),
        on: { closed: () => { cancelled = true; } }
    });
    dlg.open();
    try {
        const auth = await spotify.waitLink(link.code, { isCancelled: () => cancelled });
        cancelled = true;
        try { dlg.close(); } catch (e) { /* ya cerrado */ }
        notify.toast(t('spotify.linked', { name: auth.name || 'Spotify' }), { icon: 'check_circle' });
        return auth;
    } catch (e) {
        try { dlg.close(); } catch (e2) { /* ya cerrado */ }
        if (e.code === 'cancelled') return null;
        notify.error(t(e.code === 'expired' ? 'spotify.linkExpired' : e.code === 'denied' ? 'spotify.denied' : 'spotify.failed'));
        return null;
    }
}

function askToConnect(info, fallbackUrl) {
    dialog({
        title: t('detail.spotify'),
        text: t('spotify.intro'),
        buttons: [
            { text: t('spotify.connect'), bold: true, onClick: async () => { const a = await linkSpotify(); if (a) playLinked(info, fallbackUrl); } },
            fallbackUrl ? { text: t('spotify.open'), onClick: () => openExternal(fallbackUrl) } : null,
            { text: t('common.cancel') }
        ]
    });
}

function onError(e, info, fallbackUrl, albumUrl) {
    const open = albumUrl || fallbackUrl;
    if (e.code === 'unlinked') {
        notify.error(t('spotify.expired'));
        if (info) askToConnect(info, fallbackUrl);
        return;
    }
    if (e.code === 'premium') {
        dialog({ title: t('detail.spotify'), text: t('spotify.premium'), buttons: [open ? { text: t('spotify.open'), bold: true, onClick: () => openExternal(open) } : null, { text: t('common.close') }] });
        return;
    }
    notify.error(t('spotify.failed'));
}

async function activeDeviceName() {
    try {
        const d = (await spotify.devices()).find((x) => x.is_active);
        return d ? d.name : '';
    } catch (e) {
        return '';
    }
}

function chooseDevice(album, info, fallbackUrl) {
    const app = window.app;
    spotify.devices().catch(() => []).then((devs) => {
        if (!devs.length) {
            dialog({ title: t('detail.spotify'), text: t('spotify.noDevice'), buttons: [{ text: t('spotify.open'), onClick: () => openExternal(album.url || fallbackUrl) }, { text: t('common.close') }] });
            return;
        }
        app.actions.create({
            buttons: [
                [{ text: t('spotify.pickDevice'), label: true }].concat(devs.map((d) => ({
                    text: d.name + (d.is_active ? ' ✓' : ''),
                    onClick: async () => {
                        try {
                            await spotify.transferAndPlay(d.id, album.uri);
                            notify.toast(t('spotify.playing', { album: album.name, device: d.name }), { icon: 'music_note', duration: 3500 });
                        } catch (e) {
                            onError(e, info, fallbackUrl, album.url);
                        }
                    }
                }))),
                [{ text: t('common.cancel'), color: 'red' }]
            ]
        }).open();
    });
}

async function playLinked(info, fallbackUrl) {
    const title = String(info.originalTitle || info.tmdbTitle || info.name || '').trim();
    const composer = String(info.composer || '').split(',')[0].trim();
    notify.toast(t('spotify.searching'), { icon: 'search', duration: 1500 });
    let album;
    try {
        album = await spotify.findAlbum({ title, composer });
    } catch (e) {
        return onError(e, info, fallbackUrl);
    }
    if (!album) {
        dialog({ title: t('detail.spotify'), text: t('spotify.notFound'), buttons: [fallbackUrl ? { text: t('spotify.open'), bold: true, onClick: () => openExternal(fallbackUrl) } : null, { text: t('common.close') }] });
        return;
    }
    try {
        await spotify.play(album.uri);
        const dev = await activeDeviceName();
        notify.toast(t('spotify.playing', { album: album.name, device: dev || 'Spotify' }), { icon: 'music_note', duration: 3500 });
    } catch (e) {
        if (e.code === 'no_device') return chooseDevice(album, info, fallbackUrl);
        onError(e, info, fallbackUrl, album.url);
    }
}

/** Botón de la ficha. `fallbackUrl` es el enlace de búsqueda en Spotify (lo que se abre si no hay Spotify Connect). */
export async function playSoundtrack(info, { fallbackUrl = '' } = {}) {
    const enabled = await spotify.configured();
    if (!enabled) {
        if (fallbackUrl) openExternal(fallbackUrl);
        return;
    }
    if (!spotify.linked()) return askToConnect(info, fallbackUrl);
    return playLinked(info, fallbackUrl);
}

/** Fila de Ajustes: conectar o desconectar. Devuelve una promesa que se resuelve cuando cambia algo. */
export function toggleSpotifyLink() {
    if (spotify.linked()) {
        return new Promise((resolve) => {
            notify.confirm(t('spotify.unlink'), t('spotify.setting'), () => { spotify.unlink(); resolve(true); }, () => resolve(false));
        });
    }
    return linkSpotify().then((a) => !!a);
}
